import { useEffect, useState, useCallback } from 'react'
import { lumi } from '@/lib/lumi'
import {CheckCircle, XCircle, Trash2, Loader2, Quote, Clock, MessageSquarePlus} from 'lucide-react'
import toast from 'react-hot-toast'
import type { Testimonial } from '@/types'
import { useAuth, canEdit, canDelete } from '@/hooks/useAuth'

const TEAL = '#5BBDB5'
const GOLD = '#C8922A'
const NAVY = '#1E3A4A'

type Filter = 'all' | 'pending' | 'approved' | 'rejected'

export default function ManageTestimonials() {
  const { user } = useAuth()
  const allowEdit   = canEdit(user, 'depoimentos')
  const allowDelete = canDelete(user, 'depoimentos')
  const [testimonials, setTestimonials] = useState<Testimonial[]>([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState<Filter>('pending')
  const [actionId, setActionId] = useState<string | null>(null)

  const fetchTestimonials = useCallback(async () => {
    setLoading(true)
    try {
      const filterQuery = filter === 'all' ? {} : { status: filter }
      const { list } = await lumi.entities.testimonials.list({
        filter: filterQuery,
        sort: { createdAt: -1 },
        limit: 100,
      })
      setTestimonials(list as Testimonial[])
    } catch (e) {
      console.error('Erro ao carregar depoimentos:', e)
      toast.error('Erro ao carregar depoimentos.')
    } finally {
      setLoading(false)
    }
  }, [filter])

  useEffect(() => { fetchTestimonials() }, [fetchTestimonials])

  const handleAction = async (id: string, status: 'approved' | 'rejected') => {
    setActionId(id)
    try {
      await lumi.entities.testimonials.update(id, {
        status,
        updatedAt: new Date().toISOString(),
      })
      toast.success(status === 'approved' ? 'Depoimento aprovado!' : 'Depoimento reprovado.')
      await fetchTestimonials()
    } catch {
      toast.error('Erro ao atualizar depoimento.')
    } finally {
      setActionId(null)
    }
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Tem certeza que deseja excluir este depoimento?')) return
    setActionId(id)
    try {
      await lumi.entities.testimonials.delete(id)
      toast.success('Depoimento excluído.')
      await fetchTestimonials()
    } catch {
      toast.error('Erro ao excluir.')
    } finally {
      setActionId(null)
    }
  }

  const counts = {
    all: testimonials.length,
    pending: testimonials.filter(t => t.status === 'pending').length,
    approved: testimonials.filter(t => t.status === 'approved').length,
    rejected: testimonials.filter(t => t.status === 'rejected').length,
  }

  const filterLabels: Record<Filter, string> = {
    all: 'Todos',
    pending: 'Aguardando',
    approved: 'Aprovados',
    rejected: 'Reprovados',
  }

  const statusColors: Record<string, string> = {
    pending: GOLD,
    approved: TEAL,
    rejected: '#ef4444',
  }

  const statusLabels: Record<string, string> = {
    pending: 'Aguardando',
    approved: 'Aprovado',
    rejected: 'Reprovado',
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Cabeçalho */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Depoimentos da Comunidade</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Aprove ou reprove os depoimentos enviados pelo público. Apenas os aprovados aparecem no site.
        </p>
      </div>

      {/* Dica */}
      <div
        className="flex items-start gap-3 rounded-xl p-4 mb-6"
        style={{ background: '#DCF0EE', border: `1px solid ${TEAL}40` }}
      >
        <MessageSquarePlus size={18} style={{ color: TEAL, flexShrink: 0, marginTop: 2 }} />
        <p className="text-sm" style={{ color: '#1e6b66' }}>
          Os visitantes do site podem enviar depoimentos pelo botão na landing page. Eles chegam aqui com status <strong>Aguardando</strong> e só aparecem no site após sua aprovação.
        </p>
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap gap-2 mb-6">
        {(Object.keys(filterLabels) as Filter[]).map(f => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className="px-4 py-2 rounded-lg text-sm font-semibold transition-all border"
            style={
              filter === f
                ? { background: TEAL, color: '#fff', borderColor: TEAL }
                : { background: 'transparent', color: '#6b7280', borderColor: '#e5e7eb' }
            }
          >
            {filterLabels[f]}
            {f === 'pending' && counts.pending > 0 && (
              <span
                className="ml-2 px-1.5 py-0.5 rounded-full text-xs font-bold text-white"
                style={{ background: GOLD }}
              >
                {counts.pending}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Lista */}
      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 size={28} className="animate-spin" style={{ color: TEAL }} />
        </div>
      ) : testimonials.length === 0 ? (
        <div className="text-center py-16">
          <Quote size={40} className="mx-auto mb-3 text-gray-300" />
          <p className="text-gray-400 text-sm">Nenhum depoimento {filter !== 'all' ? `"${filterLabels[filter].toLowerCase()}"` : ''} encontrado.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {testimonials.map(t => (
            <div
              key={t._id}
              className="bg-white dark:bg-gray-900 rounded-2xl border p-5 transition-all"
              style={{ borderColor: t.status === 'pending' ? `${GOLD}60` : '#e5e7eb' }}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  {/* Status badge */}
                  <span
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold mb-3"
                    style={{
                      background: statusColors[t.status] + '18',
                      color: statusColors[t.status],
                    }}
                  >
                    {t.status === 'pending' && <Clock size={11} />}
                    {t.status === 'approved' && <CheckCircle size={11} />}
                    {t.status === 'rejected' && <XCircle size={11} />}
                    {statusLabels[t.status]}
                  </span>

                  {/* Texto */}
                  <Quote size={18} className="mb-2" style={{ color: '#d1d5db' }} />
                  <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed italic mb-3">
                    {t.text}
                  </p>

                  {/* Autor */}
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0" style={{ background: TEAL }}>
                      {(t as any).photoUrl ? (
                        <img src={(t as any).photoUrl} alt={t.authorName} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-white font-bold text-xs">
                          {t.authorName.charAt(0).toUpperCase()}
                        </div>
                      )}
                    </div>
                    <div>
                      <div className="text-sm font-bold text-gray-800 dark:text-gray-200">{t.authorName}</div>
                      {t.authorRole && <div className="text-xs text-gray-400">{t.authorRole}</div>}
                    </div>
                    <span className="text-xs text-gray-300 ml-2">
                      {new Date(t.createdAt).toLocaleDateString('pt-BR')}
                    </span>
                  </div>
                </div>

                {/* Ações */}
                <div className="flex flex-col gap-2 flex-shrink-0">
                  {allowEdit && t.status !== 'approved' && (
                    <button
                      onClick={() => handleAction(t._id, 'approved')}
                      disabled={actionId === t._id}
                      className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold text-white transition-all disabled:opacity-50"
                      style={{ background: TEAL }}
                      title="Aprovar"
                    >
                      {actionId === t._id ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle size={12} />}
                      Aprovar
                    </button>
                  )}
                  {allowEdit && t.status !== 'rejected' && (
                    <button
                      onClick={() => handleAction(t._id, 'rejected')}
                      disabled={actionId === t._id}
                      className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold transition-all disabled:opacity-50 text-red-600 border border-red-200 hover:bg-red-50"
                      title="Reprovar"
                    >
                      {actionId === t._id ? <Loader2 size={12} className="animate-spin" /> : <XCircle size={12} />}
                      Reprovar
                    </button>
                  )}
                  {allowDelete && (
                    <button
                      onClick={() => handleDelete(t._id)}
                      disabled={actionId === t._id}
                      className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold text-gray-400 hover:text-red-500 hover:bg-red-50 transition-all disabled:opacity-50"
                      title="Excluir"
                    >
                      <Trash2 size={12} />
                      Excluir
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
